import { useEffect, useState } from "react";
import { Download, Share, SquarePlus, X } from "lucide-react";

import { Button } from "@/components/ui/button";

/**
 * Aviso para instalar a plataforma como app no celular (PWA).
 *
 * iPhone: a Apple não deixa o site abrir a instalação — só dá para mostrar o
 * caminho (Compartilhar › Adicionar à Tela de Início). Android: o Chrome
 * entrega o `beforeinstallprompt` e o botão abre a instalação de verdade.
 *
 * Fica no App, fora do login, de propósito: no iPhone o app instalado não
 * enxerga o login feito no Safari, então quem instala antes de entrar só
 * entra uma vez.
 *
 * Nas páginas que o cliente abre (o link da apresentação e o simulador público)
 * o aviso não aparece: o app é da equipe, não do cliente (Rick, 17/10/2026).
 */

const DISPENSADO_KEY = "north:instalar-app-dispensado";

type EventoInstalar = Event & { prompt: () => Promise<void> };

export const estaInstalado = (): boolean =>
  window.matchMedia?.("(display-mode: standalone)").matches === true ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const ehIphone = (): boolean => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  // iPad com iPadOS 13+ se apresenta como Mac; o toque é o que entrega.
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
};

/** Telas abertas por quem não é da equipe: o link da apresentação e o /simule. */
export const ehPaginaDoCliente = (hash: string): boolean => /^#\/(estudo|simule)(\/|\?|$)/.test(hash);

const foiDispensado = (): boolean => {
  try {
    return localStorage.getItem(DISPENSADO_KEY) === "1";
  } catch {
    return false;
  }
};

export const InstalarApp = () => {
  const [dispensado, setDispensado] = useState(foiDispensado);
  const [eventoAndroid, setEventoAndroid] = useState<EventoInstalar | null>(null);
  const [instalado, setInstalado] = useState(estaInstalado);
  const [doCliente, setDoCliente] = useState(() => ehPaginaDoCliente(window.location.hash));
  const iphone = ehIphone();

  useEffect(() => {
    const aoOferecer = (e: Event) => {
      e.preventDefault();
      setEventoAndroid(e as EventoInstalar);
    };
    const aoInstalar = () => {
      setInstalado(true);
    };
    const aoNavegar = () => setDoCliente(ehPaginaDoCliente(window.location.hash));
    window.addEventListener("beforeinstallprompt", aoOferecer);
    window.addEventListener("appinstalled", aoInstalar);
    window.addEventListener("hashchange", aoNavegar);
    return () => {
      window.removeEventListener("hashchange", aoNavegar);
      window.removeEventListener("beforeinstallprompt", aoOferecer);
      window.removeEventListener("appinstalled", aoInstalar);
    };
  }, []);

  const android = !iphone && eventoAndroid !== null && /Android/.test(navigator.userAgent);
  if (instalado || dispensado || doCliente || (!iphone && !android)) return null;

  const dispensar = () => {
    try {
      localStorage.setItem(DISPENSADO_KEY, "1");
    } catch {
      // localStorage bloqueado: some só nesta visita.
    }
    setDispensado(true);
  };

  const instalarAndroid = async () => {
    await eventoAndroid?.prompt();
    // O evento só vale uma vez; aceito ou não, o aviso sai.
    setEventoAndroid(null);
  };

  return (
    <div
      role="dialog"
      aria-label="Instalar o app North"
      className="fixed inset-x-3 z-40 mx-auto flex max-w-sm items-start gap-3 rounded-xl border border-[rgba(53,70,96,0.15)] bg-card p-3 shadow-lg"
      style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
    >
      <img src="/icons/icon-192.png" alt="" className="h-10 w-10 shrink-0 rounded-lg" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">Instalar o app North</p>
        {iphone ? (
          <p className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              Compartilhar <Share className="h-3.5 w-3.5" aria-hidden />
            </span>
            <span aria-hidden>›</span>
            <span className="inline-flex items-center gap-1">
              Adicionar à Tela de Início <SquarePlus className="h-3.5 w-3.5" aria-hidden />
            </span>
          </p>
        ) : (
          <Button type="button" size="sm" onClick={instalarAndroid} className="mt-2 h-7 gap-1.5">
            <Download className="h-3.5 w-3.5" />
            Instalar
          </Button>
        )}
      </div>
      <button
        type="button"
        onClick={dispensar}
        aria-label="Fechar"
        className="-m-1 rounded p-1 text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
